import React, { useCallback, useMemo } from 'react';
import { Select, SelectOptionProps } from '@televet/kibble-ui/build/components/Select';
import { FormTemplateType, useGetAutomationFormTemplateOptionsQuery } from 'shared/types/graphql';
import { GraphQLFetchPolicies } from 'shared/enums/GraphQLFetchPolicies';
import { AutomationsPromptBlockFieldContainer } from 'pages/Automations/components/Layout';
import { AutomationPrompt } from 'pages/Automations/types/AutomationPrompt';
import { useAppDispatch } from 'state/hooks';
import useClinicUser from 'shared/hooks/useClinicUser';
import { updateAutomationPromptConfig, updateAutomationPromptErrors } from 'pages/Automations/state/automationsSlice';
import { DELETED, PromptFormError } from 'pages/Automations/types/AutomationValidation';
import { useGetFieldErrorText } from 'pages/Automations/hooks/useGetFieldErrorText';

interface FormsProps {
  prompt: AutomationPrompt;
  promptIndex: number;
  errors?: PromptFormError[];
}

export const Forms = ({ prompt, promptIndex, errors }: FormsProps): JSX.Element => {
  const {
    config: { formTemplateId },
  } = prompt;
  const dispatch = useAppDispatch();
  const { currentClinicId } = useClinicUser();
  const errorText = useGetFieldErrorText(errors);

  const fieldIsInvalid = useMemo(() => {
    return Boolean(errors?.length);
  }, [errors]);

  // TODO: Update this query to only be called once the menu is opened
  const { data: formTemplatesData } = useGetAutomationFormTemplateOptionsQuery({
    variables: {
      where: {
        clinicId: { equals: currentClinicId },
        isDeleted: { equals: false },
        isActive: { equals: true },
        formTemplateType: { notIn: [FormTemplateType.FillInTheBlank, FormTemplateType.WidgetRequest] },
      },
    },
    fetchPolicy: GraphQLFetchPolicies.CacheAndNetwork,
    skip: !currentClinicId,
  });

  const handleFormSelect = useCallback(
    (formOption: SelectOptionProps, clearValidation: boolean): void => {
      dispatch(updateAutomationPromptConfig({ promptIndex, config: { formTemplateId: formOption.value } }));

      if (fieldIsInvalid && clearValidation) {
        dispatch(updateAutomationPromptErrors({ promptIndex, prompt: { form: [] } }));
      }
    },
    [dispatch, fieldIsInvalid, promptIndex],
  );

  const formOptions = useMemo(() => {
    if (!formTemplatesData?.findManyFormTemplate) return [];

    const forms: SelectOptionProps[] = formTemplatesData.findManyFormTemplate.map((formTemplate) => ({
      label: formTemplate.title,
      value: formTemplate.id,
      isSelected: formTemplateId === formTemplate.id,
    }));

    // If the selected form has been deleted add "deleted" option
    if (formTemplateId) {
      // TODO: Fetch deleted forms and filter them out here on the client,
      // allowing us to show the actual name of the deleted form (appending something like "(Deleted)" to the name)
      const formExists = formTemplatesData?.findManyFormTemplate.find((form) => form.id === formTemplateId);
      if (!formExists) {
        const deletedOption: SelectOptionProps = {
          label: '[Deleted Form]',
          value: DELETED,
          isDisabled: true,
          variant: 'default',
        };

        handleFormSelect(deletedOption, false);
        forms.push(deletedOption);
      }
    }

    return forms;
  }, [formTemplatesData, handleFormSelect, formTemplateId]);

  return (
    <AutomationsPromptBlockFieldContainer label="Form">
      <Select
        value={formTemplateId || undefined}
        options={formOptions}
        isInvalid={fieldIsInvalid}
        errorText={errorText}
        placeholder="Select Form"
        listProps={{
          maxH: '300px',
          onSelect: (selectedItem: SelectOptionProps): void => handleFormSelect(selectedItem, true),
          isSearchable: true,
        }}
      />
    </AutomationsPromptBlockFieldContainer>
  );
};
